// The signal check: how often a W × D footprint at each center frequency
// would meet observed power.
//
// Scope is the clip's slots and bins, read from the run directly (never the
// compressed grid). The noise floor is the whole run's per-bin median. Work is
// one pass over rows with per-row prefix sums, so every candidate costs O(1)
// per row and the whole pass is O(rows × (bins + candidates)); memory is
// O(bins + candidates × K), never rows × bins.
//
// There is no ranking here. An earlier version suggested the three quietest
// windows; that was dropped in favor of scoring every center and letting the
// reader run the pointer along the chart, because "quietest" and "where I can
// actually transmit" are different questions and only the reader knows the
// second.

import { MISSING, type Run } from '../model.ts'
import type { Clip } from '../render/layout.ts'
import { binAttenuationCenti, budgetSpanHz, insertionLossDb, type ResolvedFilter } from './filter.ts'

export type Threshold = { kind: 'relative'; marginDb: number } | { kind: 'absolute'; db: number }

export interface ProfileOptions {
  clip: Clip
  widthHz: number
  airtimeS: number
  threshold: Threshold
  /**
   * The station's receive filter, or null. It cuts both ways: an interferer
   * down the skirt is one this receiver would barely hear, so it should not
   * count against a center, and a center down the skirt is one our own
   * transmission would pay for, so the chart says so.
   */
  filter?: ResolvedFilter | null
  /** How much insertion loss our own signal may take, in dB. Ignored without a filter. */
  lossBudgetDb?: number
}

export interface Candidate {
  centerHz: number
  /** The bin this center sits on, or -1 for a center that is not a bin center. */
  bin: number
}

export interface CenterScore extends Candidate {
  /** Fraction of present start rows whose D-long window meets an occupied cell. */
  score: number
  /** Fraction of present rows with any occupied cell in the span; the K=1 view. */
  occupancy: number
  /** Mean dB above the floor over present cells in the span, as the filter leaves them. */
  excessDb: number
  presentRows: number
  bin0: number
  bin1: number
  /** What our own transmission here would lose to the filter; 0 without one. */
  filterLossDb: number
}

export interface CollisionProfile {
  /**
   * Score at every bin of the crop: index `i` is bin `bin0 + i`. NaN where the
   * width could not fit or no data covered it.
   */
  scores: Float32Array
  bin0: number
  rows: number
  presentRows: number
  /** Rows a transmission of the given airtime spans. */
  windowRows: number
  /** Centers scored: the bins the width fits at. */
  evaluated: number
  /** Of those, how many keep our own signal inside the loss budget. */
  inBudget: number
  /** The centers the budget allows, for the chart; null when no filter is in play. */
  budget: { lowHz: number; highHz: number } | null
  /** The worst score anywhere, so the chart can say so. */
  peak: number
}

/** First index with `binHz[i] >= target`. */
function lowerBound(binHz: Float64Array, target: number): number {
  let lo = 0
  let hi = binHz.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (binHz[mid]! < target) lo = mid + 1
    else hi = mid
  }
  return lo
}

/** First index with `binHz[i] > target`. */
function upperBound(binHz: Float64Array, target: number): number {
  let lo = 0
  let hi = binHz.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (binHz[mid]! <= target) lo = mid + 1
    else hi = mid
  }
  return lo
}

/** Bins of `run` a span [lowHz, highHz] covers, intersected with the clip's crop. */
export function spanBins(clip: Clip, lowHz: number, highHz: number): [number, number] {
  const b0 = Math.max(clip.bin0, lowerBound(clip.run.binHz, lowHz - clip.offsetHz))
  const b1 = Math.min(clip.bin1, upperBound(clip.run.binHz, highHz - clip.offsetHz))
  return [b0, b1]
}

/**
 * Every bin center in the crop at which the full width still fits inside it.
 * The check is deliberately free of any channel scheme: a center is any
 * frequency the data resolves.
 */
export function binCandidates(clip: Clip, widthHz: number): Candidate[] {
  const { run } = clip
  const lowEdge = run.binHz[clip.bin0]! + clip.offsetHz - run.geometry.stepHz / 2
  const highEdge = run.binHz[clip.bin1 - 1]! + clip.offsetHz + run.geometry.stepHz / 2
  const out: Candidate[] = []
  for (let b = clip.bin0; b < clip.bin1; b++) {
    const center = run.binHz[b]! + clip.offsetHz
    if (center - widthHz / 2 < lowEdge || center + widthHz / 2 > highEdge) continue
    out.push({ centerHz: center, bin: b })
  }
  return out
}

function windowRows(airtimeS: number, cadenceMs: number): number {
  return Math.max(1, Math.ceil((airtimeS * 1000) / cadenceMs))
}

/**
 * Evaluates every candidate against the clip. A cell is occupied when present
 * and above the threshold; a row is busy for a candidate when any cell in its
 * span is occupied, and present when any cell in its span has data. A start
 * row collides when any of the K rows from it is busy. Rows where the whole
 * span is MISSING count in neither numerator nor denominator.
 *
 * With a filter, readings are taken as it leaves them before the threshold is
 * applied — the same masking the plot draws. An interferer 20 dB down the
 * skirt is one this receiver would barely hear, and counting it would rule out
 * a frequency that is in practice clear.
 */
export function evaluateCandidates(
  clip: Clip,
  candidates: readonly Candidate[],
  widthHz: number,
  airtimeS: number,
  threshold: Threshold,
  filter: ResolvedFilter | null = null,
): { results: CenterScore[]; rows: number; presentRows: number; windowRows: number } {
  const run: Run = clip.run
  const cols = clip.bin1 - clip.bin0
  const K = windowRows(airtimeS, run.cadenceMs)
  const n = candidates.length

  // Per-candidate spans as offsets into the crop.
  const span0 = new Int32Array(n)
  const span1 = new Int32Array(n)
  for (let i = 0; i < n; i++) {
    const c = candidates[i]!
    const [b0, b1] = spanBins(clip, c.centerHz - widthHz / 2, c.centerHz + widthHz / 2)
    span0[i] = b0 - clip.bin0
    span1[i] = b1 - clip.bin0
  }

  // Threshold per crop bin, in centi-dB; a bin with no floor never counts as occupied.
  const thresholdCenti = new Float64Array(cols)
  for (let c = 0; c < cols; c++) {
    const bin = clip.bin0 + c
    if (threshold.kind === 'absolute') {
      thresholdCenti[c] = threshold.db * 100
    } else {
      const floor = run.floorDb[bin]!
      thresholdCenti[c] = Number.isNaN(floor) ? Infinity : (floor + threshold.marginDb) * 100
    }
  }
  const floorCenti = new Float64Array(cols)
  for (let c = 0; c < cols; c++) {
    const floor = run.floorDb[clip.bin0 + c]!
    floorCenti[c] = Number.isNaN(floor) ? 0 : floor * 100
  }

  // The filter, as the painter applies it: down to the bin's own noise floor
  // and no further, and never upward.
  const attenCenti = filter ? binAttenuationCenti(clip, filter) : null
  const maskFloorCenti = new Float64Array(cols)
  if (attenCenti) {
    for (let c = 0; c < cols; c++) {
      const floor = run.floorDb[clip.bin0 + c]!
      maskFloorCenti[c] = (Number.isNaN(floor) ? run.minDb : floor) * 100
    }
  }

  // Per-row prefix sums, reused row to row.
  const presentPrefix = new Int32Array(cols + 1)
  const busyPrefix = new Int32Array(cols + 1)
  const excessPrefix = new Float64Array(cols + 1)

  // Per-candidate accumulators.
  const busyRows = new Int32Array(n)
  const presentRows = new Int32Array(n)
  const presentCells = new Float64Array(n)
  const excessSum = new Float64Array(n)
  const collisions = new Int32Array(n)
  const presentStarts = new Int32Array(n)
  // Ring of the last K rows' busy and present flags per candidate.
  const ringBusy = new Uint8Array(n * K)
  const ringPresent = new Uint8Array(n * K)
  const ringBusyCount = new Int32Array(n)

  let rowsSeen = 0
  let rowsPresent = 0
  const rows = clip.slot1 - clip.slot0

  for (let t = 0; t < rows; t++) {
    const sweep = run.slotSweep[clip.slot0 + t]!
    const base = sweep === -1 ? -1 : sweep * run.binCount + clip.bin0

    presentPrefix[0] = 0
    busyPrefix[0] = 0
    excessPrefix[0] = 0
    let anyPresent = false
    for (let c = 0; c < cols; c++) {
      const raw = base === -1 ? MISSING : run.dbm[base + c]!
      const present = raw !== MISSING
      anyPresent = anyPresent || present
      let v = raw
      if (present && attenCenti) {
        const floor = maskFloorCenti[c]!
        v = raw - attenCenti[c]!
        if (v < floor) v = raw < floor ? raw : floor
      }
      presentPrefix[c + 1] = presentPrefix[c]! + (present ? 1 : 0)
      busyPrefix[c + 1] = busyPrefix[c]! + (present && v > thresholdCenti[c]! ? 1 : 0)
      excessPrefix[c + 1] = excessPrefix[c]! + (present ? v - floorCenti[c]! : 0)
    }
    rowsSeen++
    if (anyPresent) rowsPresent++

    const slot = t % K
    for (let i = 0; i < n; i++) {
      const a = span0[i]!
      const b = span1[i]!
      const present = b > a && presentPrefix[b]! - presentPrefix[a]! > 0
      const busy = present && busyPrefix[b]! - busyPrefix[a]! > 0
      if (present) {
        presentRows[i]! += 1
        presentCells[i]! += presentPrefix[b]! - presentPrefix[a]!
        excessSum[i]! += excessPrefix[b]! - excessPrefix[a]!
        if (busy) busyRows[i]! += 1
      }

      // Slide the window: drop the flag K rows back, add this row's.
      const idx = i * K + slot
      ringBusyCount[i]! -= ringBusy[idx]!
      ringBusy[idx] = busy ? 1 : 0
      ringPresent[idx] = present ? 1 : 0
      ringBusyCount[i]! += ringBusy[idx]!

      // The window ending at this row starts at t - K + 1; count it once complete.
      if (t >= K - 1) {
        const startSlot = (t - K + 1) % K
        if (ringPresent[i * K + startSlot]) {
          presentStarts[i]! += 1
          if (ringBusyCount[i]! > 0) collisions[i]! += 1
        }
      }
    }
  }

  // Starts too near the end for a full window: evaluate what remains of it.
  // The loop above counted starts 0 .. rows-K, so these never double count,
  // and when the clip is shorter than K rows this is where every start lands.
  for (let s = Math.max(0, rows - K + 1); s < rows; s++) {
    for (let i = 0; i < n; i++) {
      if (!ringPresent[i * K + (s % K)]) continue
      let busy = 0
      for (let r = s; r < rows; r++) busy += ringBusy[i * K + (r % K)]!
      presentStarts[i]! += 1
      if (busy > 0) collisions[i]! += 1
    }
  }

  const results: CenterScore[] = candidates.map((c, i) => ({
    centerHz: c.centerHz,
    bin: c.bin,
    score: presentStarts[i]! > 0 ? collisions[i]! / presentStarts[i]! : NaN,
    occupancy: presentRows[i]! > 0 ? busyRows[i]! / presentRows[i]! : NaN,
    excessDb: presentCells[i]! > 0 ? excessSum[i]! / presentCells[i]! / 100 : NaN,
    presentRows: presentRows[i]!,
    bin0: clip.bin0 + span0[i]!,
    bin1: clip.bin0 + span1[i]!,
    filterLossDb: filter ? insertionLossDb(c.centerHz, widthHz, filter) : 0,
  }))

  return { results, rows: rowsSeen, presentRows: rowsPresent, windowRows: K }
}

/** The score at every bin center of the crop: the chart across the foot of the plot. */
export function collisionProfile(options: ProfileOptions): CollisionProfile {
  const { clip, widthHz, airtimeS, threshold } = options
  const filter = options.filter ?? null
  const budgetDb = options.lossBudgetDb ?? Infinity
  const candidates = binCandidates(clip, widthHz)
  const { results, rows, presentRows, windowRows: K } = evaluateCandidates(
    clip,
    candidates,
    widthHz,
    airtimeS,
    threshold,
    filter,
  )

  // Every center's score in bin order, including the ones the budget rules
  // out. The deep stopband is always the quietest place on the plot and always
  // the worst place to transmit, because the same skirt that hid the neighbors
  // would eat our own signal; the chart draws that part faint rather than
  // dropping it, because the shape of the interference there is still true.
  const scores = new Float32Array(clip.bin1 - clip.bin0).fill(NaN)
  let peak = 0
  let inBudget = 0
  for (const r of results) {
    if (r.bin >= 0) scores[r.bin - clip.bin0] = r.score
    if (r.score > peak) peak = r.score
    if (!filter || r.filterLossDb <= budgetDb) inBudget++
  }

  return {
    scores,
    bin0: clip.bin0,
    rows,
    presentRows,
    windowRows: K,
    evaluated: candidates.length,
    inBudget,
    budget: filter ? budgetSpanHz(filter, widthHz, budgetDb) : null,
    peak,
  }
}

/**
 * The profile's score at the bin nearest a frequency, NaN where there is none.
 * What the readout shows as the pointer moves: a lookup, not a second pass
 * over the rows, so it costs nothing per frame.
 */
export function scoreAt(profile: CollisionProfile, clip: Clip, hz: number): number {
  const { binHz } = clip.run
  const target = hz - clip.offsetHz
  let bin = lowerBound(binHz, target)
  if (bin > 0 && (bin >= binHz.length || target - binHz[bin - 1]! < binHz[bin]! - target)) bin--
  const i = bin - profile.bin0
  return i >= 0 && i < profile.scores.length ? profile.scores[i]! : NaN
}

/** The same metrics for one center, for the pinned signal's readout. */
export function evaluateCenter(
  clip: Clip,
  centerHz: number,
  widthHz: number,
  airtimeS: number,
  threshold: Threshold,
  filter: ResolvedFilter | null = null,
): CenterScore | null {
  const { results } = evaluateCandidates(clip, [{ centerHz, bin: -1 }], widthHz, airtimeS, threshold, filter)
  const r = results[0]
  return r && !Number.isNaN(r.score) ? r : null
}
